import { HYDRATION_REWRITE_MONITOR_SCRIPT } from './hydration-rewrite-monitor';
import type { SsrTrace } from './ssr-trace';

/** The deployment values the bootstrap stashes on `window` for the client to read back. */
export interface BootstrapRuntimeEnv {
	readonly cdnUrl: string;
	readonly imgproxyUrl: string;
	readonly analyticsCode: string;
	readonly recaptchaSiteKey: string;
	readonly adyenClientKey: string;
	readonly usercentricsSettingsId: string;
	/** `'true'` on every stage but `pro`; see the `data-draft` note in `loadUsercentrics`. */
	readonly usercentricsDraft: string;
}

/**
 * The origins the document preconnects to: always these two, always in this order.
 *
 * WHY A FUNCTION AND NOT TWO CONDITIONAL `<link>`s (TEC-3825). They used to be written inline in
 * `__root.tsx` as `{cdnUrl && <link …/>}` and `{imgproxyUrl && imgproxyUrl !== cdnUrl && <link …/>}`,
 * and both conditions read the runtime stash. The server always has it; the client only has it if the
 * bootstrap script ran. When it did not, the client's head had TWO FEWER CHILDREN than the server's,
 * every later entry shifted by a position, and React threw the tree away — the same `#418 args[]=HTML`
 * failure {@link bootstrapHeadScripts} exists to prevent, reintroduced 200 lines below the comment
 * that forbids it.
 *
 * So the count is fixed and the href is allowed to be empty. An empty `href` resolves to the document's
 * own URL, and preconnecting to an origin the browser is already connected to does nothing — an inert
 * element is the cheap half of this trade, and a shifted head is the expensive half. A duplicate when
 * `imgproxyUrl === cdnUrl` is likewise inert; deduplicating it is what made the element conditional.
 *
 * The href still differs between server and client when the stash is missing, but an ATTRIBUTE
 * mismatch is patched silently by React — it is the child count that throws.
 */
export function headPreconnects(
	env: Pick<BootstrapRuntimeEnv, 'cdnUrl' | 'imgproxyUrl'>,
): readonly { readonly key: string; readonly href: string }[] {
	return [
		{ key: 'cdn', href: env.cdnUrl },
		{ key: 'imgproxy', href: env.imgproxyUrl },
	];
}

/**
 * The trace this document was rendered in, as an inline script.
 *
 * Emitted even when there is no trace, as empty ids. The client rebuilds this text from the values it
 * reads back off `window`, so both cases round-trip byte-identically: real ids come back as themselves,
 * and empty ids come back as empty (`currentDocumentTrace` treats empty as absent, and this writes the
 * same empty strings again). Never made conditional — see {@link bootstrapHeadScripts}.
 */
function initTraceScript(trace: SsrTrace | undefined): string {
	return (
		`window.__TRACE_ID=${JSON.stringify(trace?.traceId ?? '')};` +
		`window.__ROOT_SPAN_ID=${JSON.stringify(trace?.rootSpanId ?? '')};`
	);
}

/**
 * Defines `globalThis.__assetUrl` for Vite's `renderBuiltUrl` runtime form (see vite.config.ts) and
 * stashes the deployment env vars on `window` so the rest of the app (router, image, recaptcha, adyen)
 * resolves them at runtime instead of build time. Must execute before the entry module loads any chunk.
 *
 * Its text has to stay byte-identical between server and client: TanStack's `Script` dedups by
 * comparing the text it renders against the inline scripts already in the document, so any divergence
 * makes the comparison fail and the whole bootstrap is re-injected and re-executed on every hydration.
 * That is why the per-request trace ids get their own script rather than being folded in here.
 */
function initAssetUrlScript(env: BootstrapRuntimeEnv): string {
	return (
		`window.__CDN_URL=${JSON.stringify(env.cdnUrl)};` +
		`window.__IMGPROXY_URL=${JSON.stringify(env.imgproxyUrl)};` +
		`window.__ANALYTICS_CODE=${JSON.stringify(env.analyticsCode)};` +
		`window.__GOOGLE_RECAPTCHA_SITE_KEY=${JSON.stringify(env.recaptchaSiteKey)};` +
		`window.__ADYEN_CLIENT_KEY=${JSON.stringify(env.adyenClientKey)};` +
		`window.__USERCENTRICS_SETTINGS_ID=${JSON.stringify(env.usercentricsSettingsId)};` +
		`window.__USERCENTRICS_DRAFT=${JSON.stringify(env.usercentricsDraft)};` +
		`globalThis.__assetUrl=function(f){var b=window.__CDN_URL||'';return (b?b.replace(/\\/$/,'')+'/':'/')+f}`
	);
}

/**
 * Google Consent Mode v2 default states: everything denied until the donor says otherwise.
 *
 * Required of the WEBSITE, not the CMP. Usercentrics' Consent Mode feature is enabled for this
 * configuration and emits the `ads_data_redaction` / developer-id signals and the `consent` `update`
 * once the donor answers, but its own panel states: "Make sure to implement the consent mode script
 * including the default states on your website." Without this, gtag.js initialises with Google's
 * built-in default — granted — so GA measures the whole first visit before the banner is answered.
 *
 * Must be queued before gtag.js reads it. That is guaranteed here rather than merely likely: this runs
 * in the head pre-hydration, while `loadGtag` injects the tag from an effect after hydration commits.
 *
 * Takes no arguments on purpose — a constant string round-trips byte-identically between server and
 * client for free, which is the invariant {@link bootstrapHeadScripts} exists to protect.
 *
 * Note this does NOT stop the tag loading — nothing here can, since it runs before there is a decision to
 * act on. What stops it is the consent gate on `loadGtag` in `__root.tsx`, which is where "no GA4 traffic
 * before consent" is actually enforced. These defaults are the second line: they guarantee that whenever
 * the tag DOES load it initialises denied rather than on Google's built-in granted, and they are what an
 * external privacy audit checked and passed.
 *
 * This used to be the only line. The tag loaded unconditionally and self-restricted, which is Google's own
 * recommendation ("advanced" Consent Mode) and preserves cookieless pings and conversion modelling. The
 * audit rejected it: storage-denied still contacts `google-analytics.com/g/collect`, and the contact was
 * the finding.
 */
function initConsentModeScript(): string {
	return (
		'window.dataLayer=window.dataLayer||[];' +
		'window.gtag=window.gtag||function(){window.dataLayer.push(arguments)};' +
		'window.gtag("consent","default",{' +
		'ad_storage:"denied",' +
		'ad_user_data:"denied",' +
		'ad_personalization:"denied",' +
		'analytics_storage:"denied",' +
		// Largely moot now that the tag only loads once consent is granted: by then the CMP's `update` is
		// already in `dataLayer`, so there is nothing left to wait for. Kept as the backstop for the one path
		// where the tag loads without a banner — an embed, where `grantEmbeddedConsent` queues its `update`
		// in the same commit as the loader.
		//
		// Worth recording why it is no longer load-bearing, because it was the mechanism that failed. The
		// window was meant to hold an early event until the donor answered, and was sized for the CMP's
		// arrival chain: queued at head parse, but `loadUsercentrics` fires from an effect after hydration,
		// then fetches `loader.js`, then the CMP itself, then restores the stored decision. The audit's
		// network capture settled it — the first `collect` left at ~3.9s with the banner still open. No
		// achievable window covers a donor who reads before clicking, and none covers one who denies.
		'wait_for_update:3000' +
		'});'
	);
}

/**
 * The inline scripts the document head bootstraps with, in execution order.
 *
 * **Every entry must stay INLINE (hence `string[]`) and unconditional.** Both properties are what keep
 * the head immune to a hydration mismatch, and the reason is specific to how React hydrates `<head>`:
 *
 * - TanStack renders an inline script as `dangerouslySetInnerHTML` + `suppressHydrationWarning`, so
 *   React neither compares its text (the text check short-circuits on `suppressHydrationWarning`) nor
 *   runs its leftover-children check (`shouldSetTextContent` is true for `dangerouslySetInnerHTML`).
 *   An inline script therefore absorbs any positional shift silently.
 * - A script with a `src` and no children gets neither exemption: `shouldSetTextContent` is false, so
 *   React runs the leftover-children check on it. Pair it with an inline script — which has a text
 *   node — and that text node is an unconsumed child, so React throws #418 `args[]=HTML` against a
 *   `<script>` inside `<head>` and regenerates the tree.
 * - `<head>` itself is a HostSingleton, which React excludes from that check, so leftovers sitting
 *   directly in the head are ignored. Only a shift that lands a text-bearing inline script in a
 *   childless script's position can raise the error.
 *
 * That is exactly what reached production on five builds: something inserted a `<script>` into the
 * head ahead of these (Instagram's in-app WebView in most of the affected sessions, which injects its
 * own JS), every rendered script paired with the wrong DOM element, the three inline ones stayed quiet,
 * and the fourth — the Google Analytics tag, the only one with a `src` and no children — raised #418.
 * Keeping this list inline-only means head injection can no longer produce a hydration error at all;
 * the analytics tag now loads after hydration instead, see `loadGtag`.
 *
 * Unconditional matters for a second, independent reason: the conditions once guarding these entries
 * were read from the `window` stash these very scripts write, so anything stopping one from taking
 * effect flipped a condition and dropped an entry on the client only — the same shift, self-inflicted.
 *
 * Order matters: the trace ids first so they are set before any chunk runs, then the runtime env, then
 * the Consent Mode defaults, then the rewrite recorder — which must stay LAST because it has to be
 * ahead of the entry module, and that module is `async` and on a slow device runs after a page
 * translator has already rewritten the text. Note the recorder watches `characterData` only, so it
 * cannot witness an injected script; a `replacements=0` report does not mean the head was untouched.
 */
export function bootstrapHeadScripts(env: BootstrapRuntimeEnv, trace: SsrTrace | undefined): readonly string[] {
	return [initTraceScript(trace), initAssetUrlScript(env), initConsentModeScript(), HYDRATION_REWRITE_MONITOR_SCRIPT];
}
